import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./ImageDialog.css";

const ImageDialog = ({ isOpen, onClose, onInsert }) => {
  const cropperRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const aspectRatios = [
    { label: "1:1", value: 1 },
    { label: "16:9", value: 16/9 },
    { label: "4:3", value: 4/3 },
    { label: "3:2", value: 3/2 },
    { label: "自由", value: 0 }
  ];

  /**
   * 處理檔案選擇
   */
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * 打開檔案選擇器
   */
  const openFileSelector = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = handleFileSelect;
    fileInput.click();
  };

  /**
   * 處理拖放
   */
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  /**
   * 設置寬高比
   */
  const setRatio = (ratio) => {
    setAspectRatio(ratio);
    if (cropperRef.current) {
      cropperRef.current.cropper.setAspectRatio(ratio === 0 ? NaN : ratio);
    }
  };

  /**
   * 重置裁切器
   */
  const resetCropper = () => {
    if (cropperRef.current) {
      cropperRef.current.cropper.reset();
    }
  };

  /**
   * 插入圖片
   */
  const handleInsert = async () => {
    if (!cropperRef.current) return;

    setIsProcessing(true);
    try {
      const cropper = cropperRef.current.cropper;
      const canvas = cropper.getCroppedCanvas({
        width: 800,
        height: 600,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });
      
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      console.log('插入圖片 DataURL:', croppedDataUrl.substring(0, 100) + '...'); // 調試信息
      onInsert(croppedDataUrl);
      handleClose();
    } catch (error) {
      console.error('圖片處理失敗:', error);
      alert('圖片處理失敗，請重試');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 關閉對話框
   */
  const handleClose = () => {
    setImageSrc(null);
    setAspectRatio(1);
    onClose();
  };

  /**
   * 處理點擊背景關閉
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const overlay = (
    <div className="image-dialog-overlay" onClick={handleOverlayClick}>
      <div className="image-dialog">
        <div className="image-dialog-header">
          <h3>插入圖片</h3>
          <button 
            className="image-dialog-close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="image-dialog-content">
          {!imageSrc ? (
            <div 
              className="image-upload-area"
              onClick={openFileSelector}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="upload-icon">📷</div>
              <div className="upload-text">點擊或拖放圖片到此處</div>
              <div className="upload-hint">支援 JPG、PNG、GIF、WebP 格式</div>
            </div>
          ) : (
            <div className="image-cropper-container">
              <div className="cropper-wrapper">
                <Cropper
                  src={imageSrc}
                  style={{ height: "100%", width: "100%" }}
                  initialAspectRatio={aspectRatio === 0 ? NaN : aspectRatio}
                  guides={true}
                  viewMode={1}
                  background={false}
                  responsive={true}
                  autoCropArea={0.8}
                  checkOrientation={false}
                  ref={cropperRef}
                />
              </div>
              
              <div className="cropper-controls">
                {aspectRatios.map((ratio) => (
                  <button
                    key={ratio.label}
                    className={`ratio-btn ${aspectRatio === ratio.value ? 'active' : ''}`}
                    onClick={() => setRatio(ratio.value)}
                  >
                    {ratio.label}
                  </button>
                ))}
                <button
                  className="ratio-btn"
                  onClick={resetCropper}
                >
                  重置
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="image-dialog-actions">
          <button 
            className="image-btn secondary"
            onClick={handleClose}
          >
            取消
          </button>
          {imageSrc && (
            <>
              <button 
                className="image-btn secondary"
                onClick={() => setImageSrc(null)}
              >
                重新選擇
              </button>
              <button 
                className="image-btn primary"
                onClick={handleInsert}
                disabled={isProcessing}
              >
                {isProcessing ? '處理中...' : '插入圖片'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return overlay;
  }

  return createPortal(overlay, document.body);
};

export default ImageDialog;
